<template>
	<div class="quote-form" :class="{ 'in-porgress': isInProgress }">
		<div class="form-container" v-if="!isInProgress">
			<div class="title-header">
				<rt-picture class="icon-request" src="/assets/app/class/icon-infinite-rocket.svg" :autosize="false"/>
				<div class="title-wwrapper">
					<h2 class="form-title">Request a quote</h2>
					<p class="form-description">For a school/district plan</p>
				</div>
			</div>
			<div class="form-wrapper">
				<div class="input-wrapper">
					<rt-input placeholder="Your name" floatingLabel="Your name" :borderError="errors.name.error" v-model="form_data.name"/>
					<p class="input-error" v-if="errors.name.error">{{ errors.name.message }}</p>
				</div>
				<div class="input-wrapper">
					<rt-input placeholder="School/District name" floatingLabel="School/District name" :borderError="errors.school_name.error" v-model="form_data.school_name"/>
					<p class="input-error" v-if="errors.school_name.error">{{ errors.school_name.message }}</p>
				</div>
				<div class="input-wrapper">
					<rt-input placeholder="Number of students" floatingLabel="Number of students" type="number" :borderError="errors.students.error" v-model="form_data.students"/>
					<p class="input-error" v-if="errors.students.error">{{ errors.students.message }}</p>
				</div>
				<div class="input-wrapper">
          <rt-input
            placeholder="Phone number (optional)"
            floatingLabel="Phone number (optional)"
            type="tel"
            :borderError="errors.phone.error"
            v-model="form_data.phone"
          />
					<p class="input-error" v-if="errors.phone.error">{{ errors.phone.message }}</p>
				</div>
				<div class="input-wrapper">
					<rt-input placeholder="Email" floatingLabel="Email" :borderError="errors.email.error" v-model="form_data.email"/>
					<p class="input-error" v-if="errors.email.error">{{ errors.email.message }}</p>
				</div>
			</div>
			<div class="button-wrapper">
				<primary-button class="btn-submit" @click="handleClickSubmit">Submit</primary-button>
			</div>
		</div>
		<rt-picture class="icon-loading" src="/assets/app/class/icon-loading.svg" :autosize="false" v-else/>
	</div>
</template>

<script>

import _ from 'lodash';
import RtPicture from '../common/Picture.vue';
import RtInput from '../common/Input.vue';
import PrimaryButton from '../common/PrimaryButton.vue';
import { validateFullusername, validateEmail, validatePhoneNumber } from '../signup/validators';
import Toast from 'common/Toast';
import Utils from 'common/Utils';
import { submitQuote } from 'common/api';

const initial_errors = {
	name: {
		error: false,
		message: ''
	},
	school_name: {
		error: false,
		message: ''
	},
	students: {
		error: false,
		message: ''
	},
	phone: {
		error: false,
		message: ''
	},
	email: {
		error: false,
		message: ''
	}
}

export default {
	props: {
		username: String,
		schoolName: String,
		email: String
	},
	components: {
		RtPicture,
		RtInput,
		PrimaryButton
	},
	data() {
		return {
			isInProgress: false,
			form_data: {
				name: '',
				school_name: '',
				students: '',
				phone: '',
				email: '',
        ref: ""
			},
			errors: _.cloneDeep(initial_errors)
		}
	},
  watch: {
    schoolName(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.initForm();
      }
    }
  },
	mounted() {
    this.initForm();
	},
	methods: {
    initForm() {
      this.form_data = {
        ...this.form_data,
        name: this.username,
        school_name: this.schoolName,
        email: this.email,
        ref: Utils.getRef()
      }
    },
		async validateForm() {
			let isValid = true;
			this.errors = _.cloneDeep(initial_errors);

			if (!this.form_data.name) {
				this.errors.name.error = true;
				this.errors.name.message = 'Name is required';
				isValid = false;
			} else {
				const isValidName = validateFullusername(this.form_data.name);
				if (isValidName !== true)	{
					isValid = false;
					this.errors.name.error = true;
					this.errors.name.message = 'Name cannot contain symbols';
				}
			}

			if (!this.form_data.school_name) {
				this.errors.school_name.error = true;
				this.errors.school_name.message = 'School/District name is required';
				isValid = false;
			}

			if (!this.form_data.students) {
				this.errors.students.error = true;
				this.errors.students.message = 'Number of students is required';
				isValid = false;
			}

      if (this.form_data.phone) {
        const validResult = validatePhoneNumber(this.form_data.phone);
        if (validResult !== true) {
          this.errors.phone.error = true;
          this.errors.phone.message = validResult;
          isValid = false;
        }
      }

			if (!this.form_data.email) {
				this.errors.email.error = true;
				this.errors.email.message = 'Email is required';
				isValid = false;
			} else {
				const isValidEmail = await validateEmail(this.form_data.email, false);
				if (isValidEmail !== true)	{
					this.errors.email.error = true;
					this.errors.email.message = 'Email address is incorrect';
					isValid = false;
				}
			}

			return isValid === true;
		},
		async handleClickSubmit() {
			const isValid = await this.validateForm();

			try {
				if (isValid) {
					this.isInProgress = true;

					const success = await submitQuote(this.form_data);
					if (success) {
						this.$emit('onSuccess');
					} else {
						Toast.error('Submit failed');
					}
				}
			} catch (e) {
				console.log(e);
				Toast.error('Something went wrong');
			} finally {
				this.isInProgress = false;
			}
		}
	}
}

</script>

<style lang="scss" scoped>

@import '../common/variables';

.quote-form {
	width: 440px;
	&.in-porgress {
		width: auto;
	}
	.title-header {
		display: flex;
		align-items: center;
		.icon-request {
			width: 56px;
			margin-right: 15px;
		}
		.title-wrapper {
			.form-title {
				font-family: $title-font;
				font-weight: $font-bold;
				font-size: 30px;
			}
			.form-description {
				color: $title-grey;
			}
		}
	}

	.form-wrapper {
		margin-top: 40px;
		.input-wrapper {
			margin-top: 30px;
			&:first-child {
				margin-top: 0;
			}
			::v-deep(input[type=number]) {
				border: 0 solid transparent;
				border-bottom: 1px solid transparent;
				&::-webkit-outer-spin-button,
				&::-webkit-inner-spin-button {
					-webkit-appearance: none;
					margin: 0;
				}
				/* Firefox */
				-moz-appearance: textfield;

				&:focus, &:hover {
					background: $light-blue;
				}
				&:hover {
					border: 0 solid transparent;
				}
				&:focus {
					background-color: transparent;
				}
			}

			.input-error {
				margin-top: 5px;
				font-size: 12px;
				color: $red;
			}
		}
	}

	.button-wrapper {
		display: flex;
		justify-content: center;
		margin-top: 40px;
		.btn-submit {
			width: 160px;
			margin: 0;
			padding-top: 1rem;
			border: 0;
			text-align: center;
		}
	}

	.icon-loading {
		width: 98px;
		animation: load8 2s infinite linear;
	}
}

</style>